import type { ActorType } from "@/lib/analytics/actor";
import {
  ACTIVATE_PATH,
  HOME_PATH,
  ONBOARDING_PATH
} from "@/lib/navigation/routes";
import { safeRedirectTarget } from "@/lib/navigation/safe-redirect";
import { normalizePathname } from "@/lib/support";
import type { HttpTypes } from "@medusajs/types";

/**
 * The first-login gate (KAN-293, KAN-306): two steps a salon passes once, in
 * order, before it uses the app - set a password (KAN-306), then save a default
 * shipping address (KAN-293). The address step is currently switched off, see
 * {@link ADDRESS_ONBOARDING_ENABLED}.
 *
 * A salon that has never saved a default shipping address cannot check out:
 * local checkout pre-selects the default shipping address and the Shopify
 * external-checkout prefill reads `is_default_shipping`. Asking for it once,
 * right after activation, guarantees both when the step is on. The decisions live here, as plain
 * functions, so `authRoute` stays a thin wrapper and the rules are testable
 * without a router.
 */

/**
 * Routes the gate must let through.
 *
 * `/onboarding` and `/activate` are the gate's own destinations - redirecting
 * either to itself would loop. `/activate` (set a password) comes *before*
 * onboarding in the journey: a brand-new salon has no address either, and
 * bouncing it away would send it to the address form with no password chosen.
 */
const EXEMPT_PATHS: readonly string[] = [ONBOARDING_PATH, ACTIVATE_PATH];

/** Whether `pathname` is one the gate never redirects away from. */
export function isFirstLoginGateExempt(pathname: string): boolean {
  return EXEMPT_PATHS.includes(normalizePathname(pathname));
}

export interface FirstLoginGateScope {
  pathname: string;
  /**
   * Who is driving the browser, as recorded when the session was opened
   * (`readSessionActor`). `null` means it could not be established.
   */
  actorType: ActorType | null;
}

/**
 * Whether the gate has any business with this navigation - decided from the
 * path and the actor alone, so the caller can skip reading the address list
 * entirely when the answer is no.
 *
 * Three deliberate exclusions:
 * - the exempt routes (see {@link EXEMPT_PATHS});
 * - a concierge operator's session. An operator shops on a salon's behalf and
 *   must not be asked to type that salon's delivery address; the gate is for
 *   the salon, not for the person driving their cart;
 * - an actor we could not establish (`null`: storage unreadable). That is the
 *   same blind spot an operator would be in, and the two failure modes are not
 *   symmetric: letting a salon through un-gated costs one skipped form (the
 *   next browser with working storage asks), while gating an operator puts a
 *   salon-address form in front of someone who cannot meaningfully fill it.
 *   So unknown is treated like the operator - fail open, like every other
 *   uncertainty in this gate.
 */
export function firstLoginGateApplies({
  pathname,
  actorType
}: FirstLoginGateScope): boolean {
  if (isFirstLoginGateExempt(pathname)) return false;
  return isGatedActor(actorType);
}

/**
 * The actor half of {@link firstLoginGateApplies}, for callers that ask the
 * address question outside any route: only a salon's own session is asked.
 */
export function isGatedActor(actorType: ActorType | null): boolean {
  return actorType === "salon_user";
}

/** Whether one of the addresses is the default shipping address. */
export function hasDefaultShippingAddress(
  addresses: NonNullable<OnboardingDecisionInput["addresses"]>
): boolean {
  return addresses.some((address) => address.is_default_shipping);
}

export interface PasswordDecisionInput extends FirstLoginGateScope {
  /**
   * The profile's `has_password`. Anything but an explicit `false` (`undefined`
   * when the field is absent, `null`) is "unknown".
   */
  hasPassword: boolean | null | undefined;
}

/**
 * Whether this navigation must be sent to the set-password step (KAN-306): the
 * gate applies ({@link firstLoginGateApplies}) and the salon has no password.
 *
 * It comes before the address step: the salon reached the app through a
 * passwordless link, and once that link is spent (the api revokes a salon's
 * links when it sets its first password) the password is its only way back in.
 *
 * Decided from the profile `authRoute` has already read, so it costs no fetch.
 * An answer we do not have is a non-redirect, for the same reason as every other
 * uncertainty in this gate: trapping a salon behind a form is worse than letting
 * one through once.
 */
export function needsPassword({
  hasPassword,
  ...scope
}: PasswordDecisionInput): boolean {
  if (!firstLoginGateApplies(scope)) return false;
  return hasPassword === false;
}

/**
 * Whether the delivery-address step (`/onboarding`) is part of the first-login
 * gate. Intentionally switched off: salons are only asked to set a password.
 * The step, its route and its decision logic are kept - flip this to `true` to
 * bring the address ask back.
 */
export const ADDRESS_ONBOARDING_ENABLED = false;

export interface OnboardingDecisionInput extends FirstLoginGateScope {
  /** `null` when the address list could not be read. */
  addresses: ReadonlyArray<
    Pick<HttpTypes.StoreCustomerAddress, "is_default_shipping">
  > | null;
}

/**
 * Whether this navigation must be sent to the address step: the gate applies
 * ({@link firstLoginGateApplies}) and the salon has no default shipping address.
 *
 * It does not read {@link ADDRESS_ONBOARDING_ENABLED}: `authRoute` is the single
 * place that checks the switch, so it can also skip reading the address list
 * while the step is off.
 *
 * An address list we could not read is a non-redirect. Failing closed would
 * trap a salon behind a form on a transient API error, while failing open costs
 * one more visit to the gate on the next navigation.
 */
export function needsOnboarding({
  addresses,
  ...scope
}: OnboardingDecisionInput): boolean {
  if (!firstLoginGateApplies(scope)) return false;
  if (addresses === null) return false;
  return !hasDefaultShippingAddress(addresses);
}

/**
 * Where a salon goes once a gate step (set a password, or the address) is done:
 * the page it was heading to, if that is an in-app path
 * ({@link safeRedirectTarget}), otherwise home. Both steps use it, and each
 * leaves the next gate to `authRoute`, so the original target survives the chain.
 *
 * The gate's own routes are refused too, which would otherwise leave the salon
 * where it started.
 */
export function firstLoginContinueTarget(target: unknown): string {
  const safe = safeRedirectTarget(target);
  const { pathname } = new URL(safe, window.location.origin);
  return isFirstLoginGateExempt(pathname) ? HOME_PATH : safe;
}
